import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listMyWorkOrders,
  type WorkOrderSummary,
} from '../../api/brandingWorkOrderApi.js';
import {
  listPlanningCycles,
  currentCycleId,
  type PlanningCycle,
} from '../../api/vendorBrandingProfileApi.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * The vendor's own work orders, approved ones only.
 *
 * No vendor filter is offered because there is nothing to choose: the API
 * resolves the vendor from the logged-in user, so this screen can only ever
 * show that vendor's work.
 */
export function VendorWorkOrdersPage(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const { day, dayRange } = useBusinessDay();
  const [rows, setRows] = useState<readonly WorkOrderSummary[] | null>(null);
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Apart from `error`, which the list read clears on success.
  const [cyclesError, setCyclesError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listPlanningCycles(token)
      .then((c) => { if (alive) { setCycles(c); setCycleId((p) => p || currentCycleId(c)); } })
      .catch((e) => { if (alive) setCyclesError(`Financial years could not be loaded: ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    listMyWorkOrders(token, cycleId || undefined)
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) { setRows([]); setError((e as Error).message); } });
    return () => { alive = false; };
  }, [token, cycleId]);

  if (!rows) return <p>Loading…</p>;

  return (
    <main aria-label="my-work-orders">
      <h1>My work orders</h1>
      {cyclesError ? <p role="alert">{cyclesError}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      <label htmlFor="mywo-cycle">Financial year</label>
      <select
        id="mywo-cycle"
        value={cycleId}
        onChange={(e) => setCycleId(e.target.value)}
        data-testid="filter-cycle"
      >
        <option value="">All years</option>
        {cycles.map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      {rows.length === 0 && !error ? (
        <p data-testid="no-work-orders">No approved work orders yet.</p>
      ) : null}

      <DataGrid<WorkOrderSummary>
        caption="my-work-orders-table"
        rows={rows}
        getRowId={(w) => w.workOrderId}
        rowTestId={(w) => `row-${w.workOrderId}`}
        searchPlaceholder="Search work orders…"
        exportFileName="my-work-orders.csv"
        columns={[
          { key: 'woNumber', header: 'Number', value: (w) => w.woNumber },
          { key: 'subject', header: 'Subject', value: (w) => w.subject },
          { key: 'cycleCode', header: 'FY', value: (w) => w.cycleCode },
          {
            // See BrandingWorkOrderListPage: `value` is the sort/export key
            // and stays the stored day; `render` is the readable form.
            key: 'validity',
            header: 'Validity',
            value: (w) => `${day(w.validFromDate)} → ${day(w.validToDate)}`,
            render: (w) => dayRange(w.validFromDate, w.validToDate),
          },
          { key: 'woStatusCode', header: 'Status', value: (w) => w.woStatusCode },
          { key: 'totalAmount', header: 'Amount', value: (w) => w.totalAmount, render: (w) => money(Number(w.totalAmount)) },
        ]}
      />
    </main>
  );
}
